import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { forwardRef } from 'react';

import * as S from './Button.style';

interface AccessibilityProps {
  /**
   * Specifies the tab order of an element.
   * @default 0
   */
  tabIndex?: number;
  /**
   * Identifies the element (or elements) that labels the element it is applied to.
   * @default undefined
   */
  ariaLabelledBy?: string;
  /**
   * Lists the ids of the elements that describe the object.
   * @default undefined
   */
  ariaDescribedBy?: string;
  /**
   * Indicates that an element will be updated.
   * @default undefined
   */
  ariaLive?: 'assertive' | 'polite' | 'off';
  /**
   * Label for a11y
   * @default undefined
   */
  ariaLabel?: string;
}

export interface ButtonProps extends AccessibilityProps, MuiButtonProps {
  /**
   * The content of the button.
   */
  children: React.ReactNode;
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * If `true`, the button will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * If `true`, the loading indicator is shown and the button becomes disabled.
   * @default false
   */
  loading?: boolean;
  /**
   * The size of the button.
   * @default 'small'
   */
  size?: Extract<MuiButtonProps['size'], 'small' | 'large'>;
  /**
   * The type of the button.
   * @default 'button'
   */
  type?: MuiButtonProps['type'];
  /**
   * The variant to use.
   * @default 'contained'
   */
  variant?: MuiButtonProps['variant'];
  /**
   * The action when clicked.
   * @default undefined
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

function ButtonComponent(
  {
    children,
    disabled = false,
    fullWidth = false,
    loading = false,
    onClick = undefined,
    size = 'large',
    type = 'button',
    variant = 'contained',
    ariaLabel = undefined,
    tabIndex = 0,
    ariaLabelledBy = undefined,
    ariaDescribedBy = undefined,
    ariaLive = undefined,
    ...buttonProps
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  return (
    <S.Button
      {...buttonProps}
      disabled={disabled}
      fullWidth={fullWidth}
      loading={loading}
      onClick={onClick}
      ref={ref}
      size={size}
      type={type}
      variant={variant}
      aria-label={ariaLabel}
      tabIndex={tabIndex}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-live={ariaLive}
    >
      <span>{children}</span>
    </S.Button>
  );
}

export const Button = forwardRef(ButtonComponent);
